// Plan-VoiceWidgetRevisions Item 2 (2026-05-25) — article side-dock panel.
//
// In voice+avatar mode the old behaviour rendered an opened article in a
// `w-3/5` side panel that shrank the avatar to `w-2/5`, crowding the
// avatar's face during a live call. This component instead renders the
// article as a docked COLUMN that appears BESIDE a full-size avatar by
// asking the parent `widget.js` to WIDEN the whole iframe.
//
// Two render modes (decided by the `embedded` + viewport-width props the
// parent passes in):
//
//   - DOCKED (desktop ≥ sm AND embedded in a widget.js parent): on mount
//     we post `{ type: 'prodagi_widget_dock_expand', extra }` to the
//     parent; widget.js widens `wrapper.style.width` (clamped to the
//     viewport) and sets its `isDocked` flag so a browser resize doesn't
//     snap back. On unmount we post `{ type: 'prodagi_widget_dock_collapse' }`
//     and widget.js restores `savedWidth`. The column slides in from the
//     right. The avatar (rendered by the parent) keeps its full width.
//
//   - OVERLAY (mobile < sm OR NOT embedded — e.g. Playground preview,
//     direct /widget/:id tab): there is no widget.js to widen the iframe,
//     so we render the article full-screen over the voice surface
//     (`absolute inset-0`), matching the pre-existing overlay reader.
//
// The article body itself is the shared `HelpCenterArticleView` in both
// modes — only the framing differs. PROTECTED feature #2 (article cards /
// HELP_CENTER_CARD) reading path is preserved: "Read" still opens the
// article; this only restructures WHERE it renders in voice mode.

import React, { useEffect, useState } from 'react';
import HelpCenterArticleView from '../Widget/HelpCenterArticleView';

interface ArticleDockPanelProps {
  agentId: string;
  slug: string;
  brandColor: string;
  isDark: boolean;
  /** True when running inside a `widget.js` parent iframe (real customer
   *  embed) — NOT the Playground preview and NOT a direct top-level tab.
   *  Only then can we widen the iframe, so only then do we dock. */
  embedded: boolean;
  onBack: () => void;
}

// Width (px) of the docked article column. Sent to widget.js as the
// `extra` to add on top of the user's current base widget width. widget.js
// clamps the total to the viewport, so on a narrow desktop the column may
// effectively render smaller — the avatar column always wins the base.
const DOCK_COLUMN_WIDTH = 360;

const ArticleDockPanel: React.FC<ArticleDockPanelProps> = ({
  agentId,
  slug,
  brandColor,
  isDark,
  embedded,
  onBack,
}) => {
  // Desktop detection mirrors widget.js's MOBILE_BREAKPOINT (480). Below
  // that, or when not embedded, we can't widen the iframe → overlay mode.
  const [isDesktop, setIsDesktop] = useState<boolean>(
    typeof window !== 'undefined' ? window.innerWidth >= 480 : true,
  );
  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 480);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // slide-in flip — false on first paint, true one frame later so the CSS
  // transition runs from off-screen-right (docked) / translucent (overlay).
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    const raf = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  const docked = embedded && isDesktop;

  // NOTE: the parent iframe dock-widen (post `prodagi_widget_dock_expand` /
  // `_collapse`) is NO LONGER driven here. It moved up to VoicePlayground as
  // a SINGLE SOURCE OF TRUTH (2026-06-29) so the compact cards panel ALSO
  // gets the widen, and so switching between the cards panel and an opened
  // article doesn't collapse→re-expand and flicker the iframe. This
  // component just renders the docked column (its width owned by the parent
  // flex row) or the overlay; the parent decides whether the iframe is wide.

  if (docked) {
    // Docked column — beside the avatar. The parent flex row gives this
    // its width; we cap it so the avatar keeps the rest. Slide in from the
    // right.
    return (
      <div
        className={`relative h-full flex-shrink-0 border-l overflow-hidden transition-transform duration-300 ease-out ${
          isDark ? 'bg-black border-gray-700' : 'bg-white border-gray-200'
        } ${entered ? 'translate-x-0' : 'translate-x-full'}`}
        style={{ width: DOCK_COLUMN_WIDTH }}
      >
        <HelpCenterArticleView
          agentId={agentId}
          slug={slug}
          brandColor={brandColor}
          isDark={isDark}
          onBack={onBack}
        />
      </div>
    );
  }

  // Overlay — mobile or non-embedded. Full-screen over the voice surface.
  return (
    <div
      className={`absolute inset-0 z-50 transition-opacity duration-200 ${
        entered ? 'opacity-100' : 'opacity-0'
      }`}
    >
      <HelpCenterArticleView
        agentId={agentId}
        slug={slug}
        brandColor={brandColor}
        isDark={isDark}
        onBack={onBack}
      />
    </div>
  );
};

export default ArticleDockPanel;
